import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import {
  RecurringTask,
  AsanaProject,
  AsanaSection,
  AsanaUser,
  getProjects,
  getSections,
  getUsers,
} from '../api/client';

interface TaskDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (task: Omit<RecurringTask, 'id' | 'created_at' | 'last_success' | 'next_run'>) => void;
  task?: RecurringTask | null;
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Lundi' },
  { value: 1, label: 'Mardi' },
  { value: 2, label: 'Mercredi' },
  { value: 3, label: 'Jeudi' },
  { value: 4, label: 'Vendredi' },
  { value: 5, label: 'Samedi' },
  { value: 6, label: 'Dimanche' },
];

const MONTHS = [
  { value: 1, label: 'Janvier' },
  { value: 2, label: 'Fevrier' },
  { value: 3, label: 'Mars' },
  { value: 4, label: 'Avril' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Juin' },
  { value: 7, label: 'Juillet' },
  { value: 8, label: 'Aout' },
  { value: 9, label: 'Septembre' },
  { value: 10, label: 'Octobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Decembre' },
];

const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, i) => ({
  value: i + 1,
  label: `${i + 1}`,
}));

const TaskDialog: React.FC<TaskDialogProps> = ({ open, onClose, onSave, task }) => {
  const [name, setName] = useState('');
  const [recurrenceType, setRecurrenceType] = useState('daily');
  const [recurrenceDay, setRecurrenceDay] = useState<number | null>(null);
  const [recurrenceMonth, setRecurrenceMonth] = useState<number | null>(null);
  const [projectGid, setProjectGid] = useState('');
  const [projectName, setProjectName] = useState('');
  const [sectionGid, setSectionGid] = useState('');
  const [sectionName, setSectionName] = useState('');
  const [assigneeGid, setAssigneeGid] = useState('');
  const [assigneeName, setAssigneeName] = useState('');
  const [dueDate, setDueDate] = useState<Dayjs | null>(null);
  const [scheduledDate, setScheduledDate] = useState<Dayjs | null>(null);

  const [projects, setProjects] = useState<AsanaProject[]>([]);
  const [sections, setSections] = useState<AsanaSection[]>([]);
  const [users, setUsers] = useState<AsanaUser[]>([]);

  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingSections, setLoadingSections] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      loadInitialData();
      if (task) {
        setName(task.name);
        setRecurrenceType(task.recurrence_type);
        setRecurrenceDay(task.recurrence_day);
        // Extract month from next_run for quarterly/biannual/annual
        if (["quarterly", "biannual", "annual"].includes(task.recurrence_type) && task.next_run) {
          setRecurrenceMonth(new Date(task.next_run).getMonth() + 1);
        }
        setProjectGid(task.project_gid);
        setProjectName(task.project_name || '');
        setSectionGid(task.section_gid || '');
        setSectionName(task.section_name || '');
        setAssigneeGid(task.assignee_gid || '');
        setAssigneeName(task.assignee_name || '');
        setDueDate(task.due_date ? dayjs(task.due_date) : null);
        if (task.recurrence_type === 'once' && task.next_run) {
          setScheduledDate(dayjs(task.next_run));
        }
      } else {
        resetForm();
      }
    }
  }, [open, task]);

  useEffect(() => {
    if (projectGid) {
      loadSections(projectGid);
    } else {
      setSections([]);
      setSectionGid('');
      setSectionName('');
    }
  }, [projectGid]);

  const resetForm = () => {
    setName('');
    setRecurrenceType('daily');
    setRecurrenceDay(null);
    setRecurrenceMonth(null);
    setProjectGid('');
    setProjectName('');
    setSectionGid('');
    setSectionName('');
    setAssigneeGid('');
    setAssigneeName('');
    setDueDate(null);
    setScheduledDate(null);
  };

  const loadInitialData = async () => {
    setError('');
    setLoadingProjects(true);
    setLoadingUsers(true);

    try {
      const [projectsRes, usersRes] = await Promise.all([
        getProjects(),
        getUsers(),
      ]);
      setProjects(projectsRes.data);
      setUsers(usersRes.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur lors du chargement des donnees Asana. Verifiez vos parametres.');
    } finally {
      setLoadingProjects(false);
      setLoadingUsers(false);
    }
  };

  const loadSections = async (projectId: string) => {
    setLoadingSections(true);
    try {
      const response = await getSections(projectId);
      setSections(response.data);
    } catch (err) {
      console.error('Error loading sections:', err);
    } finally {
      setLoadingSections(false);
    }
  };

  const handleProjectChange = (gid: string) => {
    const project = projects.find((p) => p.gid === gid);
    setProjectGid(gid);
    setProjectName(project?.name || '');
    setSectionGid('');
    setSectionName('');
  };

  const handleSectionChange = (gid: string) => {
    const section = sections.find((s) => s.gid === gid);
    setSectionGid(gid);
    setSectionName(section?.name || '');
  };

  const handleAssigneeChange = (gid: string) => {
    const user = users.find((u) => u.gid === gid);
    setAssigneeGid(gid);
    setAssigneeName(user?.name || '');
  };

  const handleSubmit = () => {
    if (!name || !projectGid) {
      setError('Le nom et le projet sont requis');
      return;
    }

    if (recurrenceType === 'once' && !scheduledDate) {
      setError('La date est requise');
      return;
    }

    if (['quarterly', 'biannual', 'annual'].includes(recurrenceType) && !recurrenceMonth) {
      setError('Le mois de depart est requis');
      return;
    }

    onSave({
      name,
      recurrence_type: recurrenceType,
      recurrence_day: recurrenceDay,
      project_gid: projectGid,
      project_name: projectName,
      section_gid: sectionGid || null,
      section_name: sectionName || null,
      assignee_gid: assigneeGid || null,
      assignee_name: assigneeName || null,
      due_date: dueDate ? dueDate.format('YYYY-MM-DD') : null,
      recurrence_month: recurrenceMonth,
      scheduled_date: scheduledDate ? scheduledDate.format('YYYY-MM-DD') : null,
    } as any);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{task ? 'Modifier la tache' : 'Nouvelle tache recurrente'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Nom de la tache"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
          />

          <FormControl fullWidth required>
            <InputLabel>Recurrence</InputLabel>
            <Select
              value={recurrenceType}
              label="Recurrence"
              onChange={(e) => {
                setRecurrenceType(e.target.value);
                setRecurrenceDay(null);
              }}
            >
              <MenuItem value="daily">Quotidien</MenuItem>
              <MenuItem value="weekly">Hebdomadaire</MenuItem>
              <MenuItem value="monthly">Mensuel</MenuItem>
              <MenuItem value="quarterly">Trimestriel</MenuItem>
              <MenuItem value="biannual">Bi-annuel</MenuItem>
              <MenuItem value="annual">Annuel</MenuItem>
              <MenuItem value="once">Une fois</MenuItem>
            </Select>
          </FormControl>

          {recurrenceType === 'weekly' && (
            <FormControl fullWidth required>
              <InputLabel>Jour de la semaine</InputLabel>
              <Select
                value={recurrenceDay ?? ''}
                label="Jour de la semaine"
                onChange={(e) => setRecurrenceDay(e.target.value as number)}
              >
                {DAYS_OF_WEEK.map((day) => (
                  <MenuItem key={day.value} value={day.value}>
                    {day.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {recurrenceType === 'once' && (
            <DatePicker
              label="Date d'execution"
              value={scheduledDate}
              onChange={(newValue) => setScheduledDate(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                },
              }}
            />
          )}

          {recurrenceType === 'monthly' && (
            <FormControl fullWidth required>
              <InputLabel>Jour du mois</InputLabel>
              <Select
                value={recurrenceDay ?? ''}
                label="Jour du mois"
                onChange={(e) => setRecurrenceDay(e.target.value as number)}
              >
                {DAYS_OF_MONTH.map((day) => (
                  <MenuItem key={day.value} value={day.value}>
                    {day.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {['quarterly', 'biannual', 'annual'].includes(recurrenceType) && (
            <>
              <FormControl fullWidth required>
                <InputLabel>Mois de depart</InputLabel>
                <Select
                  value={recurrenceMonth ?? ''}
                  label="Mois de depart"
                  onChange={(e) => setRecurrenceMonth(e.target.value as number)}
                >
                  {MONTHS.map((month) => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel>Jour du mois</InputLabel>
                <Select
                  value={recurrenceDay ?? ''}
                  label="Jour du mois"
                  onChange={(e) => setRecurrenceDay(e.target.value as number)}
                >
                  {DAYS_OF_MONTH.map((day) => (
                    <MenuItem key={day.value} value={day.value}>
                      {day.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          <FormControl fullWidth required>
            <InputLabel>Projet</InputLabel>
            <Select
              value={projectGid}
              label="Projet"
              onChange={(e) => handleProjectChange(e.target.value)}
              disabled={loadingProjects}
            >
              {loadingProjects ? (
                <MenuItem value="">
                  <CircularProgress size={20} />
                </MenuItem>
              ) : (
                projects.map((project) => (
                  <MenuItem key={project.gid} value={project.gid}>
                    {project.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Section/Colonne</InputLabel>
            <Select
              value={sectionGid}
              label="Section/Colonne"
              onChange={(e) => handleSectionChange(e.target.value)}
              disabled={!projectGid || loadingSections}
            >
              <MenuItem value="">
                <em>Aucune</em>
              </MenuItem>
              {loadingSections ? (
                <MenuItem value="">
                  <CircularProgress size={20} />
                </MenuItem>
              ) : (
                sections.map((section) => (
                  <MenuItem key={section.gid} value={section.gid}>
                    {section.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Assigne</InputLabel>
            <Select
              value={assigneeGid}
              label="Assigne"
              onChange={(e) => handleAssigneeChange(e.target.value)}
              disabled={loadingUsers}
            >
              <MenuItem value="">
                <em>Non assigne</em>
              </MenuItem>
              {loadingUsers ? (
                <MenuItem value="">
                  <CircularProgress size={20} />
                </MenuItem>
              ) : (
                users.map((user) => (
                  <MenuItem key={user.gid} value={user.gid}>
                    {user.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <DatePicker
            label="Date d'echeance"
            value={dueDate}
            onChange={(newValue) => setDueDate(newValue)}
            slotProps={{
              textField: {
                fullWidth: true,
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Annuler</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!name || !projectGid || (loadingProjects || loadingUsers)}
        >
          {task ? 'Modifier' : 'Creer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskDialog;
